import { Directive, Input, OnDestroy, OnInit, input } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { ViewModelProperty } from '../models';

@Directive()
export abstract class BaseEdit implements OnDestroy, OnInit {
  private _value: any = undefined;
  private _initialValue: any = undefined;

  internalControl: AbstractControl | undefined = undefined;

  readonly controlName = input('');
  readonly groupName = input('');
  readonly iconName = input('');
  readonly toolTipText = input('');
  readonly enableToolTip = input(true);
  readonly autofocus = input(false);
  readonly width = input(400);
  readonly form = input<UntypedFormGroup>(new UntypedFormGroup({}));
  readonly placeHolder = input('');
  readonly emptyDisplayValue = input('');

  subscription: Subscription = new Subscription();
  disabled = false;

  /* support for VMP binding */
  private _vmp: ViewModelProperty | undefined = undefined;

  @Input()
  get vmp(): ViewModelProperty | undefined {
    return this._vmp;
  }

  set vmp(vmpValue: ViewModelProperty | undefined) {
    if (this._vmp == vmpValue) {
      return;
    }
    this._vmp = vmpValue;
    this.subscription.add(
      this.vmp?.valueChange
        .pipe(startWith((vmpValue?.value || vmpValue?.initialValue) as unknown))
        .subscribe((next) => this.updateControlValue(next))
    );
  }

  /* Initial Value */
  @Input()
  get initialValue(): any {
    if (this.isBound) {
      // @ts-ignore vmp can not be undefined, as we check it with isBound
      return this._vmp?.initialValue;
    }
    return this._initialValue;
  }

  set initialValue(value: any) {
    if (this.isBound) {
      console.log('initial value not supported in bound mode');
      return;
    }

    if (this._initialValue == value) {
      return;
    }
    this._initialValue = value;
    if (this._value == undefined && value != undefined) {
      // initialize value vith initial value
      this._value = value;
      this.updateControlValue(value);
    }
  }

  /* Value */

  @Input()
  get value(): any {
    if (this.isBound) {
      // @ts-ignore vmp can not be undefined, as we check it with isBound
      return this._vmp.value;
    }
    return this._value;
  }

  set value(value: any) {
    this.internalSetValue(value);
    this.updateControlValue(value);
  }

  ngOnInit(): void {
    if (this.form() != undefined) {
      this.internalControl = new UntypedFormControl(this.initialValue);
      this.form().addControl(this.controlName(), this.internalControl);

      this.subscription.add(
        this.internalControl.valueChanges.subscribe((value) => {
          this.internalSetValue(value);
        })
      );
      this.subscription.add(
        this.internalControl.statusChanges.subscribe((status) => {
          this.disabled = status === 'DISABLED' ? true : false;
        })
      );
    }
    this.getValuesFromVMP();
  }

  /**
   * sets the value of the vmp or the internal value field
   * @param value
   */
  internalSetValue(value: any): void {
    if (this.isBound) {
      // @ts-ignore vmp can not be undefined, as we check it with isBound
      this._vmp.value = value;
    } else {
      this._value = value;
    }
  }

  private updateControlValue(value: any): void {
    if (this.internalControl == null) {
      return;
    }
    this.internalControl.patchValue(value);
  }

  private getValuesFromVMP(): void {
    if (!this.isBound) {
      return;
    }
    // @ts-ignore vmp can not be undefined, as we check it with isBound
    if (this._vmp.value != undefined) {
      // @ts-ignore value can not be undefined, as we check it with isBound
      this.updateControlValue(this._vmp.value);
      return;
    }
    // @ts-ignore value can not be undefined, as we check it with isBound
    if (this._vmp.initialValue != undefined) {
      // @ts-ignore value can not be undefined, as we check it with isBound
      this.updateControlValue(this._vmp.initialValue);
      return;
    }
  }

  /* methods */

  get isBound(): boolean {
    return this._vmp != undefined;
  }

  get isReadOnly(): boolean {
    return this._vmp === undefined || this._vmp.readOnly;
  }

  get style(): string {
    return this._vmp?.style ?? '';
  }

  get hasInitialValue(): boolean {
    if (this.isBound) {
      return this._vmp?.initialValue != undefined;
    }
    return (this.initialValue ?? '') != '';
  }

  hasIcon(): boolean {
    return this.iconName() != undefined && this.iconName() != '';
  }

  getToolTipText(): string {
    if (!this.enableToolTip()) {
      return '';
    }
    if (this.toolTipText() != undefined && this.toolTipText() != '') {
      // allow manual text override vmp
      return this.toolTipText();
    }

    if (this.isBound) {
      // @ts-ignore vmp can not be undefined, as we check it with isBound
      return this._vmp.infoText;
    }
    return '';
  }

  hasValue(): boolean {
    return (
      this.internalControl !== undefined && this.internalControl.value !== null && this.internalControl.value !== ''
    );
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}
